import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { UserDetailDialog } from '@/components/user-detail-dialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { api, type OffsetPage } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { formatDate } from '@/utils/format';

interface PlatformUserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: 'active' | 'suspended' | 'banned' | 'deactivated';
  platformRole: 'none' | 'platform_manager' | 'platform_admin';
  emailVerifiedAt: string | null;
  lastSignInAt: string | null;
  createdAt: string;
}

export const UsersPage = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const isAdmin = useAuthStore((state) => state.user?.platformRole) === 'platform_admin';

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['platform-users', page, pageSize, search],
    queryFn: () =>
      api.get<{ data: PlatformUserRow[]; page: OffsetPage }>('/platform/users', {
        query: { page, pageSize, ...(search ? { search } : {}), sort: '-createdAt' },
      }),
  });

  const rows = users.data?.data ?? [];
  const pageMeta = users.data?.page;

  const columns = useMemo((): DataTableColumn<PlatformUserRow>[] => {
    return [
      {
        id: 'createdAt',
        header: t('users.columns.created'),
        cell: (row) => formatDate(row.createdAt),
      },
      {
        id: 'name',
        header: t('users.columns.name'),
        cell: (row) => (
          <span className="font-medium">
            {row.firstName} {row.lastName}
          </span>
        ),
      },
      {
        id: 'email',
        header: t('users.columns.email'),
        cell: (row) => row.email,
        hideBelowMd: true,
      },
      {
        id: 'platformRole',
        header: t('users.columns.platformRole'),
        cell: (row) =>
          row.platformRole === 'none' ? (
            <span className="text-fg-secondary">—</span>
          ) : (
            <Badge variant={row.platformRole === 'platform_admin' ? 'brand' : 'info'}>
              {t(`platformRoles.${row.platformRole}`)}
            </Badge>
          ),
        hideBelowMd: true,
      },
      {
        id: 'status',
        header: t('users.columns.status'),
        cell: (row) => (
          <Badge variant={row.status === 'active' ? 'success' : 'neutral'}>
            {t(`status.${row.status}`)}
          </Badge>
        ),
      },
      {
        id: 'lastSignInAt',
        header: t('users.columns.lastSignIn'),
        cell: (row) => (row.lastSignInAt ? formatDate(row.lastSignInAt) : '—'),
        hideBelowMd: true,
      },
    ];
  }, [t]);

  return (
    <div>
      <PageHeader title={t('users.title')} description={t('users.description')} />

      {!isAdmin ? (
        <p className="mb-6 text-body-sm text-fg-secondary">{t('users.readOnlyNotice')}</p>
      ) : null}

      <div className="mb-4 max-w-md">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder={t('users.search')}
          aria-label={t('users.search')}
        />
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={users.isLoading}
        onRowClick={(row) => setSelectedUserId(row.id)}
        empty={
          <p className="py-8 text-center text-body-sm text-fg-secondary">{t('users.empty')}</p>
        }
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <UserDetailDialog
        userId={selectedUserId}
        open={selectedUserId !== null}
        onOpenChange={(open) => !open && setSelectedUserId(null)}
      />
    </div>
  );
};
